import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import type { AuthContextType, AuthState } from '../types/auth';
import type { User } from '../types/entities';
import { authApi } from '../api/auth';
import { preferencesStore } from '../lib/preferencesStore';
import { clearDraft } from '../utils/saisieDrafts';

const initialState: AuthState = {
  user: null,
  token: null,
  role: null,
  loading: true,
  isAuthenticated: false,
};

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_ERROR' }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        role: action.payload.user.role,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOGIN_ERROR':
      return { ...state, loading: false };
    case 'LOGOUT':
      return {
        ...initialState,
        loading: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const isCheckingAuth = useRef(false);

  const login = async (email: string, password: string) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await authApi.login({ email, password });
      
      // Store token and user in localStorage
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.user,
          token: response.access_token,
        },
      });
    } catch (error) {
      dispatch({ type: 'LOGIN_ERROR' });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      // The changes the Saisie kept for this account (#55) leave with the session; the page that unmounts
      // right after does not write them back (it only writes while a session is open)
      clearDraft(state.user?.id);
      preferencesStore.clear();
      dispatch({ type: 'LOGOUT' });
    }
  };

  const checkAuth = async () => {
    // Prevent multiple simultaneous auth checks
    if (isCheckingAuth.current) {
      return;
    }

    isCheckingAuth.current = true;
    
    try {
      const token = localStorage.getItem('access_token');
      const userData = localStorage.getItem('user');

      if (!token || !userData) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      // Try to parse stored user data first (faster than API call)
      try {
        const storedUser = JSON.parse(userData);
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: storedUser,
            token,
          },
        });

        // Then validate with API in background
        try {
          const user = await authApi.me();
          // Update with fresh data if different
          if (JSON.stringify(user) !== JSON.stringify(storedUser)) {
            localStorage.setItem('user', JSON.stringify(user));
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user,
                token,
              },
            });
          }
        } catch {
          // If API validation fails, clear everything
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          dispatch({ type: 'LOGOUT' });
        }
      } catch {
        // If stored user data is invalid, clear and logout
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        dispatch({ type: 'LOGOUT' });
      }
    } finally {
      isCheckingAuth.current = false;
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

